import React, { Fragment, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { CButton, CDialog } from "@appcues/component-library";

import { selectUser } from "reducers/user";
import { selectAccountUsers } from "reducers/account/users";

import CoolTip from "components/Common/CoolTip";
import InviteModal from "components/Team/InviteManager/InviteModal";
import { asPromised } from "utils/as-promised";
import { inviteUser } from "actions/account/users";
import { selectAccountFeature } from "reducers/account/features";
import { EDITOR_PERMISSIONS_ROLE } from "constants/features";
import { canManageAccountUsers } from "helpers/account-management";

export const InviteManager = ({
  onInvite,
  disabled = false,
  enableEditorRole = false
}) => {
  const [currentModal, setCurrentModal] = useState(null);
  const [invitedUser, setInvitedUser] = useState(null);

  const clearCurrentModal = useCallback(() => setCurrentModal(null), [
    setCurrentModal
  ]);

  const handleInviteUser = useCallback(
    fields => {
      setInvitedUser(fields);
      return onInvite(fields)
        .then(() => setCurrentModal("INVITE_SENT"))
        .catch(err => {
          switch (err.name) {
            case "UserExistsError":
              setCurrentModal("USER_EXISTS");
              break;
            case "UserInAccountError":
              setCurrentModal("USER_IN_ACCOUNT");
              break;
            default:
              setCurrentModal("ERROR");
          }
        });
    },
    [onInvite]
  );

  return (
    <Fragment>
      {currentModal === "INVITE" && (
        <InviteModal
          enableEditorRole={enableEditorRole}
          onSubmit={handleInviteUser}
          onClose={clearCurrentModal}
        />
      )}
      {currentModal === "USER_IN_ACCOUNT" && (
        <CDialog
          title={`${invitedUser.name} is already in this account`}
          onPrimaryAction={clearCurrentModal}
        >
          Whoops! We found a user with that email in your account already! Let
          us know if they are having trouble logging in.
        </CDialog>
      )}
      {currentModal === "INVITE_SENT" && (
        <CDialog
          title={`${invitedUser.name} has been invited to join your account`}
          onPrimaryAction={clearCurrentModal}
        >
          They will receive an e-mail to this effect with login instructions.
        </CDialog>
      )}
      {currentModal === "ERROR" && (
        <CDialog
          title="We encountered an error"
          onPrimaryAction={clearCurrentModal}
        >
          Sorry about that. Please try again, and if the problem persists, don't
          hesitate to contact us.
        </CDialog>
      )}
      <CoolTip
        tip="Only account admins can invite new users"
        disabled={!disabled}
      >
        <CButton
          type="positive"
          disabled={disabled}
          onClick={() => setCurrentModal("INVITE")}
        >
          Invite teammate
        </CButton>
      </CoolTip>
    </Fragment>
  );
};

InviteManager.propTypes = {
  onInvite: PropTypes.func,
  disabled: PropTypes.bool,
  enableEditorRole: PropTypes.bool
};

const mapStateToProps = state => {
  const user = selectUser(state);
  const accountUsers = selectAccountUsers(state);
  return {
    enableEditorRole: selectAccountFeature(state, EDITOR_PERMISSIONS_ROLE),
    disabled: !canManageAccountUsers({ user, accountUsers })
  };
};

const mapDispatchToProps = dispatch => ({
  onInvite: fields => asPromised(dispatch, inviteUser(fields))
});

export default connect(mapStateToProps, mapDispatchToProps)(InviteManager);
